import React from 'react';
import PropTypes from 'prop-types';
import classNames from '../../../utils/classNames';
import { pickCoverGradient } from '../../../utils/bandCoverGradient';
import './index.scss';

/**
 * Square, rounded band avatar.
 *
 * - With an `imageUrl`, renders the band artwork edge-to-edge.
 * - Without one, falls back to the band's deterministic cover gradient (the
 *   same family used by the bands catalog cards) so the placeholder keeps the
 *   band's visual identity. The fallback is intentionally text-free; pass
 *   `showMonogram` only where an embossed monogram is desired.
 */
export default function BandAvatar({
  imageUrl,
  seed,
  gradient,
  name = '',
  monogram = '',
  showMonogram = false,
  className = '',
}) {
  const resolvedImage = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  const hasImage = Boolean(resolvedImage);
  const resolvedGradient = gradient || pickCoverGradient(seed || name);

  return (
    <div
      className={classNames(
        'band-avatar',
        hasImage ? 'band-avatar--image' : 'band-avatar--placeholder',
        className,
      )}
      style={hasImage ? undefined : { backgroundImage: resolvedGradient }}
    >
      {hasImage ? (
        <img
          className="band-avatar__img"
          src={resolvedImage}
          alt={name ? `${name}` : ''}
          loading="eager"
          decoding="async"
        />
      ) : (
        <>
          <span className="band-avatar__sheen" aria-hidden="true" />
          {showMonogram && monogram ? (
            <span className="band-avatar__monogram" aria-hidden="true">
              {monogram}
            </span>
          ) : null}
        </>
      )}
      <span className="band-avatar__edge" aria-hidden="true" />
    </div>
  );
}

BandAvatar.propTypes = {
  /** Resolved artwork URL. When present, image wins over the gradient. */
  imageUrl: PropTypes.string,
  /** Stable seed (band objectId) used to pick the deterministic gradient. */
  seed: PropTypes.string,
  /** Explicit gradient override (CSS background-image value). */
  gradient: PropTypes.string,
  /** Band name, used for the image alt text and as a gradient seed fallback. */
  name: PropTypes.string,
  /** Optional embossed monogram for the placeholder. */
  monogram: PropTypes.string,
  /** Render the monogram inside the placeholder. Off by default (text-free). */
  showMonogram: PropTypes.bool,
  className: PropTypes.string,
};
